import { useState } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Upload, Loader2, Search, Plus, Check, X, ExternalLink, ArrowUp,
  MessageSquare, Brain, ChevronDown, ChevronUp, Zap
} from "lucide-react";

const SOURCES = [
  { id: "recent-posts", label: "Recent Posts" },
  { id: "high-engagement", label: "High Engagement" },
  { id: "google-search", label: "Google Search" },
];

export default function UploadPanel({ onRefresh, projects, selectedProject }) {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [source, setSource] = useState("recent-posts");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [vetMode, setVetMode] = useState(false);
  const [vetting, setVetting] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true); setUploadMsg(null);
    try {
      const res = await api.post("/entries/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadMsg({ type: "success", text: res.data.message });
      onRefresh?.();
    } catch (err) {
      setUploadMsg({ type: "error", text: err.response?.data?.detail || "Upload failed" });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) handleUpload(file);
    else setUploadMsg({ type: "error", text: "Please upload .xlsx or .xls file" });
  };

  const pollSearchStatus = async (taskId, maxWait = 660000) => {
    const start = Date.now();
    while (Date.now() - start < maxWait) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await api.get(`/import/search-status/${taskId}`);
        if (res.data.status === "completed") return res.data;
        if (res.data.status === "failed") throw new Error(res.data.error || "Search failed");
      } catch (err) {
        if (err.response?.status === 404) continue;
        throw err;
      }
    }
    throw new Error("Search timed out after 10 minutes");
  };

  const handleSearch = async () => {
    if (!keyword.trim()) return;
    setSearching(true); setResults([]); setSearchMeta(null); setImportMsg(null); setSelected(new Set());
    try {
      const res = await api.post("/import/search", { keyword: keyword.trim(), source });
      if (res.data.task_id) {
        const result = await pollSearchStatus(res.data.task_id);
        setResults(result.items || []);
        setSearchMeta({ count: result.count, source, keyword: keyword.trim() });
        if (result.error) {
          setImportMsg({ type: "error", text: `Scraper issue: ${result.error}` });
        }
      } else {
        setResults(res.data.items || []);
        setSearchMeta({ count: res.data.count, source: res.data.source, keyword: res.data.keyword });
      }
    } catch (err) {
      setImportMsg({ type: "error", text: err.message || err.response?.data?.detail || "Search failed" });
    } finally {
      setSearching(false);
    }
  };

  const toggleSelect = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === results.length) setSelected(new Set());
    else setSelected(new Set(results.map((_, i) => i)));
  };

  const handleDirectImport = async () => {
    const posts = results.filter((_, i) => selected.has(i));
    if (!posts.length) return;
    setImporting(true); setImportMsg(null);
    try {
      const res = await api.post("/import/add-posts", { posts, project_id: selectedProject || "" });
      setImportMsg({ type: "success", text: `Added ${res.data.added} posts (${res.data.duplicates} duplicates skipped)` });
      onRefresh?.();
      const remaining = results.filter((_, i) => !selected.has(i));
      setResults(remaining);
      setSelected(new Set());
    } catch (err) {
      setImportMsg({ type: "error", text: err.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
    }
  };

  const handleVetAndImport = async () => {
    const posts = results.filter((_, i) => selected.has(i));
    if (!posts.length) return;
    setVetting(true); setImportMsg(null);
    try {
      let context = "";
      if (selectedProject) {
        try {
          const ctxRes = await api.get(`/llm/website-context/${selectedProject}`);
          context = ctxRes.data.website_context || "";
        } catch {}
      }
      const res = await api.post("/import/vet-and-import", {
        posts,
        project_id: selectedProject || "",
        website_context: context,
      });
      const msg = `AI analyzed ${res.data.total_processed} posts: ${res.data.imported} imported, ${res.data.filtered} filtered out${res.data.errors ? `, ${res.data.errors} errors` : ''}`;
      setImportMsg({ type: "success", text: msg });
      onRefresh?.();
      const remaining = results.filter((_, i) => !selected.has(i));
      setResults(remaining);
      setSelected(new Set());
    } catch (err) {
      setImportMsg({ type: "error", text: err.response?.data?.detail || "AI vetting failed" });
    } finally {
      setVetting(false);
    }
  };

  return (
    <div className="upload-section" data-testid="upload-section">
      <div className="import-section" data-testid="keyword-search-section">
        <h2 className="section-title"><Search size={20} color="#ff4500" /> Search Reddit Posts</h2>
        <div className="search-bar" data-testid="keyword-search-bar">
          <input
            type="text" className="filter-input search-keyword-input"
            placeholder="Enter keyword (e.g. logkeys, windows key...)"
            value={keyword} onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            data-testid="keyword-input"
          />
          <div className="source-tabs">
            {SOURCES.map(s => (
              <button key={s.id} className={`source-tab ${source === s.id ? 'active' : ''}`}
                onClick={() => setSource(s.id)} data-testid={`source-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={handleSearch} disabled={searching || !keyword.trim()} data-testid="search-btn">
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {importMsg && (
          <div className={`alert alert-${importMsg.type}`} style={{margin:'12px 0'}} data-testid="import-message">
            {importMsg.type === "success" ? <Check size={14} /> : <X size={14} />} {importMsg.text}
          </div>
        )}

        {results.length > 0 && (
          <div className="search-results" data-testid="search-results">
            <div className="search-results-header">
              <div className="results-meta">
                <span className="results-count">{searchMeta?.count || results.length} posts found</span>
                <span className="results-source">via {searchMeta?.source}</span>
                <span className="results-keyword">for "{searchMeta?.keyword}"</span>
              </div>
              <div className="results-actions">
                <button className="btn btn-sm btn-secondary" onClick={toggleSelectAll} data-testid="select-all-results">
                  {selected.size === results.length ? 'Deselect All' : `Select All (${results.length})`}
                </button>
                <label className="vet-toggle" data-testid="vet-mode-toggle">
                  <input type="checkbox" checked={vetMode} onChange={e => setVetMode(e.target.checked)} />
                  <Brain size={13} /> AI Vet
                </label>
                {selected.size > 0 && !vetMode && (
                  <button className="btn btn-sm btn-primary" onClick={handleDirectImport} disabled={importing} data-testid="import-selected-btn">
                    {importing ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                    Import {selected.size} Posts
                  </button>
                )}
                {selected.size > 0 && vetMode && (
                  <button className="btn btn-sm btn-ai" onClick={handleVetAndImport} disabled={vetting} data-testid="vet-import-btn">
                    {vetting ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                    {vetting ? 'AI Analyzing...' : `AI Vet & Import ${selected.size}`}
                  </button>
                )}
              </div>
            </div>

            <div className="search-results-list">
              {results.map((post, idx) => (
                <div key={idx} className={`search-result-card ${selected.has(idx) ? 'selected' : ''}`} data-testid={`result-${idx}`}>
                  <div className="result-main" onClick={() => toggleSelect(idx)}>
                    <div className={`custom-checkbox ${selected.has(idx) ? 'checked' : ''}`}>
                      {selected.has(idx) && <Check size={12} color="white" />}
                    </div>
                    <div className="result-content">
                      <div className="result-title">{post.title || '(no title)'}</div>
                      <div className="result-meta">
                        <span className="subreddit-cell">r/{post.subreddit}</span>
                        <span className="result-author">u/{post.author}</span>
                        <span className="result-score"><ArrowUp size={11} /> {post.score}</span>
                        <span className="result-comments"><MessageSquare size={11} /> {post.num_comments}</span>
                      </div>
                    </div>
                    <div className="result-expand">
                      <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); setExpandedIdx(expandedIdx === idx ? null : idx); }}>
                        {expandedIdx === idx ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {post.url && (
                        <a href={post.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" onClick={e => e.stopPropagation()}>
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                  {expandedIdx === idx && post.body && (
                    <div className="result-body">{post.body}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {searching && (
          <div className="search-loading">
            <Loader2 size={24} className="animate-spin" color="#ff4500" />
            <span>Searching Reddit for "{keyword}"...</span>
          </div>
        )}
      </div>

      <div className="import-section" style={{marginTop: 32}}>
        <h2 className="section-title"><Upload size={20} color="#ff4500" /> Import Excel File</h2>
        {uploadMsg && <div className={`alert alert-${uploadMsg.type}`} style={{margin:'12px 0'}}>{uploadMsg.text}</div>}
        <div className={`upload-area ${dragOver ? 'drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
          data-testid="upload-area">
          <input id="file-input" type="file" accept=".xlsx,.xls" onChange={e => handleUpload(e.target.files[0])} style={{display:'none'}} />
          <div className="upload-icon">
            {uploading ? <Loader2 size={28} color="#ff4500" className="animate-spin" /> : <Upload size={28} color="#ff4500" />}
          </div>
          <div className="upload-text">{uploading ? "Uploading..." : "Drop Excel file here or click to upload"}</div>
          <div className="upload-hint">Supports .xlsx and .xls files. Duplicates are automatically filtered by URL.</div>
        </div>
      </div>
    </div>
  );
}
